<template>
  <NoteSection
    label="Medical Necessity"
    required
    :highlight-required="isSectionIncomplete"
    :form-validated="formValidated"
  >
    <div v-if="woundHealingRate === null || woundHealingRate >= .75">
      <alert>
        Since beginning treatment with Skilled Wound Care, this wound has
        reduced in area by 75% or more. This indicates that it may not be
        a good candidate for an amniotic skin substitute graft. Therefore,
        please address this in your response below. Thank you.
      </alert>
    </div>
    <p class="mb-3">
      Discuss wound chronicity and unresponsiveness to past treatments.
      Explain the medical necessity for amniotic treatment. Explain current treatment for
      underlying medical conditions and any related conditions to the ulcer. The "AI" button
      will summarize previous treatments, if present (shown below); please revise and expand
      as needed.
    </p>
    <text-area
      label="Reason"
      v-model="reason_required"
      required
      ai
      counter="5000"
      maxlength="5000"
      class="col-sm-12"
      @click:prepend="generateReason"
      @input="emitValue"
    />
    <v-divider class="col-12 mb-4"/>
    <p class="mb-3">Non-amniotic tissue graft treatments from before this wound's
      first amniotic tissue graft treatment are listed below. Note that this is for
      informational purposes only: no entries need to be made here.
    </p>
    <div
      v-if="treatmentsData && treatmentsData.length > 0"
      class="col-12"
    >
      <v-data-table
        :headers="headers"
        :items="treatmentsData"
        fixed-header
        dense
        height="250"
        class="mb-4"
        sort-by="visit_date"
        sort-desc
        hide-default-footer
        :items-per-page="treatmentsData.length"
      >
        <template #item.visit_date="{ item }">
          {{ $date(item.visit_date).format('MM/DD/YYYY') }}
        </template>

        <template #item.procedure_id="{ item }">
          <div class="text-left px-3">
            {{ $store.getters['baseData/procedureFromId'](item.procedure_id) }}
          </div>
        </template>

        <template #item.primary_area="{ item }">
        <div class="d-block pr-2">
          {{ item.primary_area }}
        </div>
      </template>
      </v-data-table>
    </div>

    <div v-else class="col-12 d-flex justify-center align-center">
      <p class="text-muted">No treatments found for this wound.</p>
    </div>

  </NoteSection>
</template>

<script>
import { amnioReqData } from '@/store'
import dayjs from 'dayjs'
import Vue from 'vue'

export default {
  name: 'AmnioMedicalNecessity',
  props: {
    value: {
      type: String,
      default: null,
    },
    woundId: {
      type: String,
      required: true
    },
    encounterVisitDate: {
      type: [String, Date],
      default: null,
    },
    woundCurrentArea: {
      type: [Number, String],
      default: null,
    },
    // Form validation state
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      treatmentsData: [],
      reason_required: this.value,
      wasAmnioTreated: null,
      firstTreatmentDate: null,
      recentTreatmentSummary: null,
      woundInitialArea: null,
      headers: [
        { text: 'Date', value: 'visit_date'},
        { text: 'Procedure', value: 'procedure_id', align: 'center'},
        { text: 'Dressing 1', value: 'dressing_used_1' },
        { text: 'Dressing 2', value: 'dressing_used_2' },
        { text: 'Other dress.', value: 'dressing_used_other' },
        { text: 'Area (cm²)', value: 'primary_area', align: 'end' }
      ]
    }
  },
  computed: {
    woundHealingRate() {
      if (this.woundInitialArea === 0 || !this.woundCurrentArea) return null
      return (this.woundInitialArea - this.woundCurrentArea) / this.woundInitialArea
    },
    // Evaluates if the Medical Necessity reason field is incomplete
    isSectionIncomplete() {
      return !this.reason_required || !this.reason_required.trim()
    },
  },
  watch: {
    reason_required(newVal) {
      // Emit the updated value to the parent whenever the value changes
      this.emitValue(newVal)
    }
  },
  async created() {
    await this.loadAllTreatments()
    if (!this.reason_required) {
      await this.generateReason()
    }
  },
  methods: {
    async loadAllTreatments() {
      const result = await amnioReqData.getItem(this.woundId)

      if (Array.isArray(result)) {
        const encounterDos = dayjs(this.encounterVisitDate).startOf('day')

        // Filter by date before current encounter's dos
        const filtered = result.filter(item => {
          const visitDate = dayjs(item.visit_date).startOf('day')
          return visitDate.isBefore(encounterDos)
        })

        // ~ ~ ~ Determine if a procedure_id 178 exists
        this.wasAmnioTreated = filtered.some(item => item.procedure_id === 178)

        // Find the first treatment and its primary area
        const { initialDate, initialArea } = filtered.reduce((acc, item) => {
          const visitDate = dayjs(item.visit_date)
          if (!acc.initialDate || visitDate.isBefore(acc.initialDate)) {
            acc.initialDate = visitDate
            acc.initialArea = item.primary_area
          }
          return acc
        }, { initialDate: null, primaryArea: null })

        this.firstTreatmentDate = initialDate
        this.woundInitialArea = initialArea

        // Sort chronologically
        const sorted = filtered.sort(
          (a, b) => new Date(a.visit_date) - new Date(b.visit_date)
        )

        // Find the first 178
        const index = sorted.findIndex(item => item.procedure_id === 178)

        // Keep everything before the 178
        const trimmed = index !== -1 ? sorted.slice(0, index) : sorted

        // Save the final list for display
        this.treatmentsData = trimmed

        // ~ ~ ~ Generate previous treatments summary text
        const formatDressings = (items) => {
          // Filter out empty values and "none"
          const filtered = items.filter(item => item && item.toLowerCase() !== 'none')

          // Handle different cases based on the number of filtered dressings
          if (filtered.length === 0) return 'with no dressings applied'
          if (filtered.length === 1) return `using dressing ${filtered[0]}`
          if (filtered.length === 2) return `using dressings ${filtered[0]} and ${filtered[1]}`
          return `using dressings ${filtered.slice(0, -1).join(', ')}, and ${filtered.slice(-1)}`
        }

        const lastThree = (trimmed
            .slice()
            .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date)) // latest first
            .slice(0, 3) // get the latest 3
          || []
        ).map(t => {
          const formattedDate = t.visit_date
            ? dayjs(t.visit_date).format('MM/DD/YYYY')
            : 'Unknown date'

          const procedure = t.procedure_id && t.procedure_id !== 999
          ? ', with procedure ' + this.$store.getters['baseData/procedureFromId'](t.procedure_id)
              .replace(/\[\d+\]/, '')   // Remove bracketed ID
              .replace(/\.$/, '')      // Remove trailing period if present
              .trim()                 // Trim whitespace
              .toLowerCase()
          : ''

          let dressings = formatDressings([
            t.dressing_used_1,
            t.dressing_used_2,
            t.dressing_used_other
          ])

          dressings = dressings ? `, ${dressings}` : '';

          return `${formattedDate}${procedure}${dressings.trim()}`
        })

        this.recentTreatmentSummary = lastThree.length
          ? lastThree.slice(0, -1).join('; ') + (lastThree.length > 1 ? '; and ' : '') + lastThree.slice(-1)
          : ''
        // ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~ ~
      } else {
        this.treatmentsData = []
        this.recentTreatmentSummary = null
      }
    },

    generateReason() {
      this.reason_required =
        (this.treatmentsData.length > 0
          ? (!this.wasAmnioTreated
            ? 'Prior to receiving amniotic tissue graft treatment, this'
            : 'This') +
          ' wound has seen treatment since ' +
          this.$date(this.firstTreatmentDate).format('MM/DD/YYYY') +
          ' (' +
          Vue.prototype.$custom.getElapsedDate(this.firstTreatmentDate, this.encounterVisitDate) +
          ') and has not responded well to methods used so far. '
          : '') +
        (this.wasAmnioTreated
          ? ''
          : 'Please note that this is the first application of an amniotic tissue graft to this wound. ') +
        (this.recentTreatmentSummary
          ? 'The patient has failed prior treatment regimens on these dates: ' + this.recentTreatmentSummary + '.'
          : '')
    },

    emitValue(newVal) {
      this.$emit('input', newVal)
    },
  }
}
</script>
